/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS } from '../programs';

export const INITIALIZE_CONFIG_DISCRIMINATOR = new Uint8Array([
  208, 127, 21, 1, 194, 190, 196, 70,
]);

export function getInitializeConfigDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    INITIALIZE_CONFIG_DISCRIMINATOR,
  );
}

export type InitializeConfigInstruction<
  TProgram extends string = typeof DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS,
  TAccountPayer extends string | AccountMeta<string> = string,
  TAccountAdminAuthority extends string | AccountMeta<string> = string,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountProgramData extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    '11111111111111111111111111111111',
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPayer extends string
        ? WritableSignerAccount<TAccountPayer> &
            AccountSignerMeta<TAccountPayer>
        : TAccountPayer,
      TAccountAdminAuthority extends string
        ? ReadonlySignerAccount<TAccountAdminAuthority> &
            AccountSignerMeta<TAccountAdminAuthority>
        : TAccountAdminAuthority,
      TAccountConfig extends string
        ? WritableAccount<TAccountConfig>
        : TAccountConfig,
      TAccountProgramData extends string
        ? ReadonlyAccount<TAccountProgramData>
        : TAccountProgramData,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type InitializeConfigInstructionData = {
  discriminator: ReadonlyUint8Array;
  cosigners: Array<Address>;
};

export type InitializeConfigInstructionDataArgs = { cosigners: Array<Address> };

export function getInitializeConfigInstructionDataEncoder(): Encoder<InitializeConfigInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['cosigners', getArrayEncoder(getAddressEncoder())],
    ]),
    (value) => ({ ...value, discriminator: INITIALIZE_CONFIG_DISCRIMINATOR }),
  );
}

export function getInitializeConfigInstructionDataDecoder(): Decoder<InitializeConfigInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['cosigners', getArrayDecoder(getAddressDecoder())],
  ]);
}

export function getInitializeConfigInstructionDataCodec(): Codec<
  InitializeConfigInstructionDataArgs,
  InitializeConfigInstructionData
> {
  return combineCodec(
    getInitializeConfigInstructionDataEncoder(),
    getInitializeConfigInstructionDataDecoder(),
  );
}

export type InitializeConfigAsyncInput<
  TAccountPayer extends string = string,
  TAccountAdminAuthority extends string = string,
  TAccountConfig extends string = string,
  TAccountProgramData extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  payer: TransactionSigner<TAccountPayer>;
  adminAuthority: TransactionSigner<TAccountAdminAuthority>;
  config?: Address<TAccountConfig>;
  programData: Address<TAccountProgramData>;
  systemProgram?: Address<TAccountSystemProgram>;
  cosigners: InitializeConfigInstructionDataArgs['cosigners'];
};

export async function getInitializeConfigInstructionAsync<
  TAccountPayer extends string,
  TAccountAdminAuthority extends string,
  TAccountConfig extends string,
  TAccountProgramData extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS,
>(
  input: InitializeConfigAsyncInput<
    TAccountPayer,
    TAccountAdminAuthority,
    TAccountConfig,
    TAccountProgramData,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  InitializeConfigInstruction<
    TProgramAddress,
    TAccountPayer,
    TAccountAdminAuthority,
    TAccountConfig,
    TAccountProgramData,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    payer: { value: input.payer ?? null, isWritable: true },
    adminAuthority: { value: input.adminAuthority ?? null, isWritable: false },
    config: { value: input.config ?? null, isWritable: true },
    programData: { value: input.programData ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.config.value) {
    accounts.config.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            99, 111, 115, 105, 103, 110, 101, 114, 95, 104, 111, 111, 107, 95,
            99, 111, 110, 102, 105, 103,
          ]),
        ),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('adminAuthority', accounts.adminAuthority),
      getAccountMeta('config', accounts.config),
      getAccountMeta('programData', accounts.programData),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getInitializeConfigInstructionDataEncoder().encode(
      args as InitializeConfigInstructionDataArgs,
    ),
    programAddress,
  } as InitializeConfigInstruction<
    TProgramAddress,
    TAccountPayer,
    TAccountAdminAuthority,
    TAccountConfig,
    TAccountProgramData,
    TAccountSystemProgram
  >);
}

export type InitializeConfigInput<
  TAccountPayer extends string = string,
  TAccountAdminAuthority extends string = string,
  TAccountConfig extends string = string,
  TAccountProgramData extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  payer: TransactionSigner<TAccountPayer>;
  adminAuthority: TransactionSigner<TAccountAdminAuthority>;
  config: Address<TAccountConfig>;
  programData: Address<TAccountProgramData>;
  systemProgram?: Address<TAccountSystemProgram>;
  cosigners: InitializeConfigInstructionDataArgs['cosigners'];
};

export function getInitializeConfigInstruction<
  TAccountPayer extends string,
  TAccountAdminAuthority extends string,
  TAccountConfig extends string,
  TAccountProgramData extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS,
>(
  input: InitializeConfigInput<
    TAccountPayer,
    TAccountAdminAuthority,
    TAccountConfig,
    TAccountProgramData,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): InitializeConfigInstruction<
  TProgramAddress,
  TAccountPayer,
  TAccountAdminAuthority,
  TAccountConfig,
  TAccountProgramData,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    payer: { value: input.payer ?? null, isWritable: true },
    adminAuthority: { value: input.adminAuthority ?? null, isWritable: false },
    config: { value: input.config ?? null, isWritable: true },
    programData: { value: input.programData ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('adminAuthority', accounts.adminAuthority),
      getAccountMeta('config', accounts.config),
      getAccountMeta('programData', accounts.programData),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getInitializeConfigInstructionDataEncoder().encode(
      args as InitializeConfigInstructionDataArgs,
    ),
    programAddress,
  } as InitializeConfigInstruction<
    TProgramAddress,
    TAccountPayer,
    TAccountAdminAuthority,
    TAccountConfig,
    TAccountProgramData,
    TAccountSystemProgram
  >);
}

export type ParsedInitializeConfigInstruction<
  TProgram extends string = typeof DOPPLER_LAUNCH_HOOK_V1_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    payer: TAccountMetas[0];
    adminAuthority: TAccountMetas[1];
    config: TAccountMetas[2];
    programData: TAccountMetas[3];
    systemProgram: TAccountMetas[4];
  };
  data: InitializeConfigInstructionData;
};

export function parseInitializeConfigInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializeConfigInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 5) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 5,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      payer: getNextAccount(),
      adminAuthority: getNextAccount(),
      config: getNextAccount(),
      programData: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getInitializeConfigInstructionDataDecoder().decode(instruction.data),
  };
}
